import {
  CSSProperties,
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
  useState,
} from "react";
import { DatePicker, DatePickerProps } from "antd";
import moment, { Moment } from "moment";
import locale from "antd/es/date-picker/locale/ja_JP";

import { DATE_TIME_FORMAT } from "@lhd/constants";
import {
  DATE_PICKER_PARSE_FORMATS,
  DATE_TIME_PICKER_PARSE_FORMATS,
  formatDatePickerSaveValue,
  isCompleteDatePickerText,
  parseDatePickerInput,
  parseDateTimePickerInput,
  toDatePickerApiDate,
} from "@lhd/utility";
import { CommonDatePickerWrap, CommonDateRangePickerWrap } from "./styled";
import type { RangePickerProps } from "antd/es/date-picker";

const datePickerLocale = {
  ...locale,
  lang: {
    ...locale.lang,
    shortWeekDays: ["日", "月", "火", "水", "木", "金", "土"],
  },
};

export type CommonDatePickerSaveFormat = "japanese" | "api";

/** Date variant that includes time. `disabledTime` and `showTime` are not on every picker mode. */
type DatePickerWithTimeProps = Extract<DatePickerProps, { picker?: "date" }>;

export type CommonDatePickerProps = {
  value?: Moment | string | Date | null;
  defaultValue?: Moment | string | Date | null;
  /** `japanese` stores 1999年2月12日. `api` stores YYYY-MM-DD. */
  saveFormat?: CommonDatePickerSaveFormat;
  /** First format is the text in the input. Typing still accepts yyyy/mm/dd. */
  displayFormat?: string;
  onChange?: (value: string | null, date: Moment | null) => void;
  /** Called when the picked day is already the stored day, so the form can recheck. */
  onUnchanged?: () => void;
  className?: string;
  popupClassName?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  wrapperStyle?: CSSProperties;
  size?: DatePickerProps["size"];
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  disabledDate?: DatePickerProps["disabledDate"];
  getPopupContainer?: DatePickerProps["getPopupContainer"];
  renderExtraFooter?: DatePickerProps["renderExtraFooter"];
  locale?: DatePickerProps["locale"];
  inputReadOnly?: DatePickerProps["inputReadOnly"];
  disabledTime?: DatePickerWithTimeProps["disabledTime"];
  picker?: DatePickerProps["picker"];
  showTime?: DatePickerWithTimeProps["showTime"];
  popupStyle?: CSSProperties;
  autoFocus?: boolean;
  open?: boolean;
  onClick?: DatePickerProps["onClick"];
  onBlur?: DatePickerProps["onBlur"];
  onOpenChange?: DatePickerProps["onOpenChange"];
  defaultPickerValue?: DatePickerProps["defaultPickerValue"];
  suffixIcon?: DatePickerProps["suffixIcon"];
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
};

const joinClass = (...names: Array<string | undefined>) =>
  names.filter(Boolean).join(" ");

export const toCommonDatePickerValue = (
  value: Moment | string | Date | null | undefined,
  saveFormat: CommonDatePickerSaveFormat,
): string | null => {
  if (value == null || value === "") return null;
  return saveFormat === "api"
    ? toDatePickerApiDate(value)
    : formatDatePickerSaveValue(value);
};

const CommonDatePicker: FC<CommonDatePickerProps> = ({
  value,
  defaultValue,
  saveFormat = "japanese",
  displayFormat = DATE_TIME_FORMAT.DATE_JP,
  onChange,
  onUnchanged,
  className,
  popupClassName,
  wrapperClassName,
  style,
  wrapperStyle,
  size,
  disabled = false,
  allowClear = true,
  /** Omit to use antd locale default (ja: 日付を選択). Pass "" for blank. */
  placeholder,
  disabledDate,
  getPopupContainer,
  renderExtraFooter,
  locale: localeProp,
  inputReadOnly,
  disabledTime,
  picker,
  showTime,
  popupStyle,
  autoFocus,
  open,
  onClick,
  onBlur: onBlurProp,
  onOpenChange,
  defaultPickerValue,
  suffixIcon,
  onKeyDown,
  onMouseDown,
}) => {
  const nativeMode = Boolean(showTime) || (picker != null && picker !== "date");
  const monthFormats = [
    displayFormat,
    "YYYY年MM月",
    "YYYY年M月",
    "YYYY/MM",
    "YYYY/M",
    "YYYY-MM",
    "YYYY-M",
  ].filter((format, index, list) => list.indexOf(format) === index);
  const dateTimeFormats = [
    displayFormat,
    ...DATE_TIME_PICKER_PARSE_FORMATS,
  ].filter((format, index, list) => list.indexOf(format) === index);
  const controlled = value !== undefined;
  const isOpenControlled = open !== undefined;
  // showTime needs controlled open so Enter can close the panel after commit.
  const manageOpen = Boolean(showTime) || isOpenControlled;
  const [innerOpen, setInnerOpen] = useState(false);
  const panelOpen = isOpenControlled ? Boolean(open) : innerOpen;
  const [innerValue, setInnerValue] = useState<string | null>(() =>
    nativeMode ? null : toCommonDatePickerValue(defaultValue, saveFormat),
  );
  const stored = nativeMode
    ? null
    : controlled
    ? toCommonDatePickerValue(value, saveFormat)
    : innerValue;
  const nativeValue = nativeMode
    ? !value
      ? null
      : moment.isMoment(value)
      ? value
      : moment(value instanceof Date ? value : String(value))
    : null;

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!isOpenControlled) setInnerOpen(next);
      onOpenChange?.(next);
    },
    [isOpenControlled, onOpenChange],
  );

  const closePanel = useCallback(() => {
    if (!isOpenControlled) setInnerOpen(false);
    onOpenChange?.(false);
  }, [isOpenControlled, onOpenChange]);

  const commit = useCallback(
    (raw: Moment | string | null) => {
      if (raw == null || (typeof raw === "string" && !raw.trim())) {
        if (!stored) return;
        if (!controlled) setInnerValue(null);
        onChange?.(null, null);
        return;
      }

      const saved = toCommonDatePickerValue(raw, saveFormat);
      if (!saved) return;
      if (saved === stored) {
        onUnchanged?.();
        return;
      }

      if (!controlled) setInnerValue(saved);
      onChange?.(saved, parseDatePickerInput(saved));
    },
    [controlled, onChange, onUnchanged, saveFormat, stored],
  );

  const commitText = useCallback(
    (raw: string, allowPartial: boolean) => {
      if (!raw.trim()) {
        if (allowPartial) commit(null);
        return;
      }
      if (!allowPartial && !isCompleteDatePickerText(raw)) return;
      commit(raw);
    },
    [commit],
  );

  const commitDateTimeText = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text) return false;
      const parsed = parseDateTimePickerInput(text);
      if (!parsed) return false;
      onChange?.(parsed.format(displayFormat), parsed);
      return true;
    },
    [displayFormat, onChange],
  );

  return (
    <CommonDatePickerWrap
      className={joinClass("common-date-picker-wrap", wrapperClassName)}
      style={wrapperStyle}
      onKeyDownCapture={(event) => {
        // Capture before antd DatePicker handles Enter (which can clear typed text).
        if (event.key !== "Enter") return;

        const input = event.currentTarget.querySelector("input");
        const raw = input?.value ?? "";

        if (!nativeMode) {
          event.preventDefault();
          event.stopPropagation();
          commitText(raw, true);
          input?.blur();
          return;
        }

        if (!showTime) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.nativeEvent.stopImmediatePropagation === "function") {
          event.nativeEvent.stopImmediatePropagation();
        }

        if (raw.trim()) {
          commitDateTimeText(raw);
        }
        closePanel();
        input?.blur();
      }}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onInput={(event) => {
        if (nativeMode) return;
        const target = event.target as HTMLInputElement;
        if (target.tagName !== "INPUT") return;
        commitText(target.value, false);
      }}
    >
      <DatePicker
        picker={picker}
        showTime={showTime}
        disabledTime={disabledTime}
        locale={localeProp || datePickerLocale}
        placeholder={placeholder}
        format={
          picker === "month"
            ? monthFormats
            : showTime
            ? dateTimeFormats
            : nativeMode
            ? displayFormat
            : [displayFormat, ...DATE_PICKER_PARSE_FORMATS]
        }
        className={joinClass("common-date-picker", className)}
        popupClassName={joinClass(
          "common-date-picker-dropdown",
          popupClassName,
        )}
        popupStyle={popupStyle}
        style={style}
        size={size}
        disabled={disabled}
        allowClear={allowClear}
        autoFocus={autoFocus}
        open={manageOpen ? panelOpen : open}
        onClick={onClick}
        onOpenChange={manageOpen ? handleOpenChange : onOpenChange}
        defaultPickerValue={defaultPickerValue}
        {...(suffixIcon !== undefined ? { suffixIcon } : {})}
        value={
          nativeMode
            ? nativeValue && nativeValue.isValid()
              ? nativeValue
              : null
            : parseDatePickerInput(stored)
        }
        disabledDate={disabledDate}
        getPopupContainer={getPopupContainer}
        renderExtraFooter={renderExtraFooter}
        inputReadOnly={inputReadOnly}
        onChange={(date) => {
          if (nativeMode) {
            onChange?.(date ? date.format(displayFormat) : null, date);
            return;
          }
          commit(date);
        }}
        onBlur={(event) => {
          if (!nativeMode) {
            commitText((event.target as HTMLInputElement).value ?? "", true);
          } else if (showTime) {
            // Commit typed text when leaving the field (panel close / tab out).
            const raw = (event.target as HTMLInputElement).value ?? "";
            if (raw.trim()) commitDateTimeText(raw);
          }
          onBlurProp?.(event);
        }}
      />
    </CommonDatePickerWrap>
  );
};

export default CommonDatePicker;

export type CommonDateRangeValue = [Moment | null, Moment | null] | null;

export type CommonDateRangePickerProps = {
  value?: CommonDateRangeValue;
  onChange?: (
    dates: CommonDateRangeValue,
    dateStrings?: [string, string],
  ) => void;
  displayFormat?: string;
  className?: string;
  popupClassName?: string;
  wrapperClassName?: string;
  style?: CSSProperties;
  wrapperStyle?: CSSProperties;
  size?: DatePickerProps["size"];
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: [string, string];
  disabledDate?: DatePickerProps["disabledDate"];
  getPopupContainer?: DatePickerProps["getPopupContainer"];
  locale?: DatePickerProps["locale"];
  inputReadOnly?: boolean;
  separator?: RangePickerProps["separator"];
};

/** Range filter: type yyyy/mm/dd, show 年月日, Enter commits. onChange still passes Moments. */
export const CommonDateRangePicker: FC<CommonDateRangePickerProps> = ({
  value,
  onChange,
  displayFormat = DATE_TIME_FORMAT.DATE_JP,
  className,
  popupClassName,
  wrapperClassName,
  style,
  wrapperStyle,
  size,
  disabled = false,
  allowClear = true,
  placeholder,
  disabledDate,
  getPopupContainer,
  locale: localeProp,
  inputReadOnly,
  separator,
}) => {
  const formats = [displayFormat, ...DATE_PICKER_PARSE_FORMATS].filter(
    (format, index, list) => list.indexOf(format) === index,
  );

  const commitFromInputs = (root: HTMLElement) => {
    const inputs = root.querySelectorAll<HTMLInputElement>(
      ".ant-picker-input input",
    );
    const startRaw = inputs[0]?.value ?? "";
    const endRaw = inputs[1]?.value ?? "";
    const startText = startRaw.trim();
    const endText = endRaw.trim();

    if (!startText && !endText) {
      onChange?.(null);
      return;
    }

    const start = startText ? parseDatePickerInput(startText) : null;
    const end = endText ? parseDatePickerInput(endText) : null;

    // Filter needs both ends; skip incomplete typed ranges.
    if (!start || !end) return;

    onChange?.(
      [start, end],
      [start.format(displayFormat), end.format(displayFormat)],
    );
  };

  return (
    <CommonDateRangePickerWrap
      className={joinClass("common-date-range-picker-wrap", wrapperClassName)}
      style={wrapperStyle}
      onKeyDownCapture={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        event.stopPropagation();
        commitFromInputs(event.currentTarget);
      }}
    >
      <DatePicker.RangePicker
        locale={localeProp || datePickerLocale}
        format={formats}
        className={joinClass("common-date-picker", className)}
        popupClassName={joinClass(
          "common-date-picker-dropdown",
          popupClassName,
        )}
        style={style}
        size={size}
        disabled={disabled}
        allowClear={allowClear}
        placeholder={placeholder}
        disabledDate={disabledDate}
        getPopupContainer={getPopupContainer}
        inputReadOnly={inputReadOnly}
        separator={separator}
        value={value ?? undefined}
        onChange={(dates, dateStrings) => {
          if (!dates || (!dates[0] && !dates[1])) {
            onChange?.(null);
            return;
          }
          onChange?.(
            [dates[0] ?? null, dates[1] ?? null],
            dateStrings as [string, string],
          );
        }}
      />
    </CommonDateRangePickerWrap>
  );
};

/** For antd Form.Item, which injects `value` and `onChange`. */
export const CommonDateFormPicker: FC<
  {
    value?: unknown;
    onChange?: (value: Moment | null) => void;
  } & Omit<CommonDatePickerProps, "value" | "onChange">
> = ({ value, onChange, displayFormat, defaultValue, ...rest }) => {
  const toMoment = (raw: unknown) => {
    if (raw == null || raw === "") return null;
    if (moment.isMoment(raw)) return raw.isValid() ? raw : null;
    if (typeof (raw as { toDate?: () => Date }).toDate === "function") {
      const m = moment((raw as { toDate: () => Date }).toDate());
      return m.isValid() ? m : null;
    }
    const m = moment(raw as Date | string);
    return m.isValid() ? m : null;
  };

  const momentValue = toMoment(value);
  const momentDefault = toMoment(defaultValue);

  return (
    <CommonDatePicker
      {...rest}
      value={value === undefined ? undefined : momentValue}
      defaultValue={momentDefault}
      displayFormat={displayFormat ?? DATE_TIME_FORMAT.DATE_JP_MD}
      onChange={(_saved, date) => onChange?.(date)}
    />
  );
};
